import dayjs, { Dayjs } from 'dayjs';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AnalyticsService from '../../services/AnalyticsService/AnalyticsService';
import ApiError from '../../types/ApiError';
import ChatbotContext from '../ChatbotContext/ChatbotContext';
import DateRangeContext from './DateRangeContext';

interface DateRangeProviderProps {
  children: React.ReactNode;
}

function DateRangeProvider({
  children
}: DateRangeProviderProps): React.ReactElement {
  const { currentChatbot } = React.useContext(ChatbotContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialDateRange = (): [Dayjs, Dayjs] => {
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');

    if (startDate && endDate) {
      return [dayjs(startDate), dayjs(endDate)];
    }

    return [dayjs().subtract(1, 'month').startOf('day'), dayjs().endOf('day')];
  };

  const [dateRange, setDateRangeState] = useState<[Dayjs, Dayjs]>(
    getInitialDateRange()
  );

  const setDateRange = useCallback(
    (newRange: [Dayjs, Dayjs]): void => {
      setDateRangeState(newRange);
      setSearchParams((params) => {
        params.set('startDate', newRange[0].format('YYYY-MM-DD'));
        params.set('endDate', newRange[1].format('YYYY-MM-DD'));
        return params;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');

    if (startDate && endDate) {
      const newStartDate = dayjs(startDate);
      const newEndDate = dayjs(endDate);

      if (
        !dateRange[0].isSame(newStartDate) ||
        !dateRange[1].isSame(newEndDate)
      ) {
        setDateRangeState([newStartDate, newEndDate]);
      }
    }
  }, [searchParams, dateRange]);

  const [firstDate, setFirstDate] = useState<Dayjs>(dayjs().startOf('day'));

  const [includeTest, setIncludeTest] = useState<boolean>(
    searchParams.get('includeTest') === 'true'
  );

  const updateIncludeTest = useCallback(
    (newIncludeTest: boolean): void => {
      setIncludeTest(newIncludeTest);
      setSearchParams((params) => {
        if (newIncludeTest) {
          params.set('includeTest', 'true');
        } else {
          params.delete('includeTest');
        }
        return params;
      });
    },
    [setSearchParams, setIncludeTest]
  );

  useEffect(() => {
    const urlIncludeTest = searchParams.get('includeTest') === 'true';
    if (includeTest && !urlIncludeTest) {
      setSearchParams((params) => {
        params.set('includeTest', 'true');
        return params;
      });
    }
  }, [searchParams, includeTest, setSearchParams]);

  const pendingChatbotIdRef = useRef<number | null>(null);
  const earliestDateCacheRef = useRef<Map<number, Dayjs>>(new Map());

  useEffect(() => {
    if (!currentChatbot?.id) return;

    const chatbotIdAtCallTime = currentChatbot.id;

    const cachedDate = earliestDateCacheRef.current.get(chatbotIdAtCallTime);
    if (cachedDate) {
      setFirstDate(cachedDate);
      setDateRangeState((prev) => {
        const [start, end] = prev;
        if (start.isBefore(cachedDate)) {
          return [cachedDate, end];
        }
        return prev;
      });
      return;
    }

    pendingChatbotIdRef.current = chatbotIdAtCallTime;

    AnalyticsService.getEarliestAnalyticsDate(chatbotIdAtCallTime)
      .then((dateStr: string) => {
        if (pendingChatbotIdRef.current !== chatbotIdAtCallTime) return;

        const d = dayjs(dateStr).startOf('day');
        earliestDateCacheRef.current.set(chatbotIdAtCallTime, d);

        setFirstDate(d);
        setDateRangeState((prev) => {
          const [start, end] = prev;
          if (start.isBefore(d)) {
            return [d, end];
          }
          return prev;
        });
      })
      .catch((err: ApiError) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load earliest analytics date', err);
      });
  }, [currentChatbot]);

  const value = React.useMemo(
    () => ({
      dateRange,
      setDateRange,
      firstDate,
      includeTest,
      setIncludeTest: updateIncludeTest
    }),
    [dateRange, setDateRange, firstDate, includeTest, updateIncludeTest]
  );

  return (
    <DateRangeContext.Provider value={value}>
      {children}
    </DateRangeContext.Provider>
  );
}

export default DateRangeProvider;
